import {
    AlertListTypeEnum,
    AlertOriginTypesEnum,
    AlertSubTypesEnum,
    EntityTypes,
    StrategyTypes,
    SystemGeneratedDocumentsTypesEnum,
} from '../../../utilities/Constants';

import { AlertViewEditActionButtons } from './AlertViewEditActionButtons';
import Authorization from '../../../stores/Authorization';
import Common from '../../../stores/Common';
import { IAlertListModel } from '../../../interfaces/INote';
import { JSX } from 'react';
import { Link } from 'react-router-dom';
import { LocalRoutes } from '../../../utilities/LocalRoutes';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useZoneContext } from '../../../contexts/useZoneContext';

const getEntityURL = (entityTypeId: number, originSubEntityType?: number) => {
    switch (entityTypeId) {
        case EntityTypes.PackageSettlement:
            return LocalRoutes.PackageSettlementDetails;
        case EntityTypes.Case:
            return LocalRoutes.CaseFile;
        case EntityTypes.Report:
            switch (originSubEntityType) {
                case SystemGeneratedDocumentsTypesEnum.WeeklyRecap.Value:
                    return LocalRoutes.WeeklyRecapCollab;
                case SystemGeneratedDocumentsTypesEnum.MonthlyRecap.Value:
                    return LocalRoutes.MonthlyRecapCollab;
                case SystemGeneratedDocumentsTypesEnum.KpiReport.Value:
                    return LocalRoutes.KpiReportCollab;
                default:
                    return '';
            }
        default:
            return '';
    }
};

const getStrategyURL = (strategyTypeId: number): string => {
    switch (strategyTypeId) {
        case StrategyTypes.Jurisdiction.Value:
            return LocalRoutes.JurisdictionStrategyDetail;
        case StrategyTypes.PlaintiffsFirm.Value:
            return LocalRoutes.PlaintiffFirmStrategyDetail;
        case StrategyTypes.LocalCounsel.Value:
            return LocalRoutes.LocalCounselStrategyDetail;
        case StrategyTypes.Projects.Value:
            return LocalRoutes.ProjectDetail;
        case StrategyTypes.Experts.Value:
            return LocalRoutes.ExpertsDetails;
        default:
            return '';
    }
};

const getNavigationUrl = (item: IAlertListModel): string => {
    let navigationUrl = '';

    if (item.originTypeId!) {
        if (item.originStrategyType === null || item.originStrategyType === undefined) {
            navigationUrl = item.originEntityType
                ? getEntityURL(item.originEntityType ?? 0, item.originSubEntityType).replace(
                      ':guid',
                      item.originEntityKey ?? ''
                  )
                : LocalRoutes.CaseFile.replace(':guid', item.originEntityKey ?? '');
        } else if (item.originStrategyType > 0) {
            navigationUrl = getStrategyURL(item.originStrategyType ?? 0).replace(
                ':guid',
                item.originEntityKey ?? ''
            );
        }
    }

    return navigationUrl;
};

export interface IAlertListRowProps {
    alertListType: AlertListTypeEnum;
    allowEasyUpdate: boolean;
    alreadyViewed: boolean;
    item: IAlertListModel;
    onAlertItemNameClick: (item: IAlertListModel) => void;
    onEasyUpdateClick: (item: IAlertListModel) => void;
    onEditClick: (item: IAlertListModel) => void;
    onMarkCaseTriggerAsReadClick: (alertGuid: string, openCaseTriggerDetailPopup?: boolean) => void;
    onViewNoteClick: (item: IAlertListModel) => void;
}

export function AlertListRow(props: IAlertListRowProps) {
    const { user } = useAuthContext();
    const { buildZoneUrl } = useZoneContext();
    const isEasyUpdate =
        props.allowEasyUpdate &&
        Authorization.isAuthorizedToRoute(LocalRoutes.CaseEasyUpdate, user);

    const buttonLayoutWidth = () => {
        return isEasyUpdate ? 160 : 112;
    };
    const nameNavigateUrl = buildZoneUrl(getNavigationUrl(props.item));

    const allowEasyUpdate = (originTypeId: number) => {
        return (
            originTypeId === AlertOriginTypesEnum.CaseNote ||
            originTypeId === AlertOriginTypesEnum.CaseTrigger
        );
    };

    return (
        <tr
            className={
                props.alertListType === AlertListTypeEnum.MyAlertList &&
                !props.item.alertReadForLoggedInUser
                    ? 'bold'
                    : ''
            }
        >
            <td>{props.item.originType}</td>
            <td>
                {nameNavigateUrl ? (
                    <Link
                        to={nameNavigateUrl}
                        target="_blank"
                        onClick={() => props.onAlertItemNameClick(props.item)}
                    >
                        {props.item.originEntityName}
                    </Link>
                ) : (
                    props.item.originEntityName
                )}
            </td>
            <td width={80}>{props.item.sqScore}</td>
            <td>
                <ul>
                    {props.item.details?.split('\n').map((value: string, i: number) => {
                        return <li key={i}>{value}</li>;
                    })}
                </ul>
            </td>
            <td>
                {props.item.purpose ? (
                    <>{props.item.purpose}</>
                ) : (
                    <ActTypeIcon subTypeId={props.item.subTypeId} />
                )}
            </td>
            <td>{props.item.decision}</td>
            <td>
                {props.item.originTypeId !== AlertOriginTypesEnum.Report
                    ? props.item.actionTargetName
                    : null}
            </td>
            <td>{props.item.writer}</td>
            <td>{Common.dateTimeFormatToLocal(props.item.modifiedDate)}</td>
            <td>{props.item.status}</td>
            <td class-name="text-nowrap" width={buttonLayoutWidth()}>
                <AlertViewEditActionButtons
                    item={props.item}
                    navigationUrl={nameNavigateUrl}
                    onEditClick={props.onEditClick}
                    onMarkCaseTriggerAsReadClick={props.onMarkCaseTriggerAsReadClick}
                    onViewNoteClick={props.onViewNoteClick}
                />
                {allowEasyUpdate(props.item.originTypeId) && isEasyUpdate && (
                    <button
                        className="btn btn-no-bg"
                        onClick={() => {
                            props.onEasyUpdateClick(props.item);
                        }}
                    >
                        <i
                            className={`fal fa-lg fa-edit ${
                                props.alreadyViewed ? 'color-gray' : 'color-black'
                            } `}
                        />
                    </button>
                )}
            </td>
        </tr>
    );
}

const ActTypeIcon = ({ subTypeId }: { subTypeId: AlertSubTypesEnum | undefined }) => {
    let actTypeComp: JSX.Element | null = null;

    switch (subTypeId) {
        case AlertSubTypesEnum.Chat:
            actTypeComp = <i className="fal fa-messages" title="Chat" />;
            break;
        case AlertSubTypesEnum.Collab:
            actTypeComp = <i className="fal fa-users" title="Collab" />;
            break;
        case AlertSubTypesEnum.Review:
            actTypeComp = <i className="fal fa-book-open-reader" title="Review" />;
            break;
    }

    return actTypeComp;
};
